/**
 * Pending Approval Page
 * Shown to external users whose registration is awaiting admin approval
 */

import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
} from '@mui/material';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { useNavigate } from 'react-router-dom';

const PendingApprovalPage = () => {
  const navigate = useNavigate();

  const handleBackToLogin = () => {
    // Clear any stored tokens and go back to login
    sessionStorage.clear();
    localStorage.removeItem('keycloak_token');
    navigate('/login');
  };

  const handleContactSupport = () => {
    window.location.href = 'mailto:support@africanalliance.com?subject=Account%20Approval%20Request';
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
        position: 'relative',
        overflow: 'hidden',
        padding: 2,
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          backgroundImage: `
            url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1200 800'%3E%3Cpath d='M0 400 Q150 320 300 420 T600 360 T900 440 T1200 380' fill='none' stroke='rgba(148,163,184,0.18)' stroke-width='1.5'/%3E%3Cpath d='M0 430 Q180 350 360 460 T720 390 T1080 470 T1200 420' fill='none' stroke='rgba(148,163,184,0.15)' stroke-width='1.2'/%3E%3Cpath d='M0 370 Q120 300 240 380 T480 340 T720 400 T960 350 T1200 370' fill='none' stroke='rgba(148,163,184,0.12)' stroke-width='1'/%3E%3Cpath d='M0 460 Q200 390 400 490 T800 420 T1200 470' fill='none' stroke='rgba(148,163,184,0.15)' stroke-width='1.2'/%3E%3Cpath d='M0 340 Q100 280 200 350 T400 310 T600 360 T800 320 T1000 370 T1200 340' fill='none' stroke='rgba(148,163,184,0.10)' stroke-width='1'/%3E%3Cpath d='M0 490 Q250 440 500 520 T1000 480 T1200 500' fill='none' stroke='rgba(148,163,184,0.12)' stroke-width='1'/%3E%3Cpath d='M0 310 Q300 260 600 330 T1200 300' fill='none' stroke='rgba(148,163,184,0.09)' stroke-width='1'/%3E%3Cpath d='M0 520 Q200 480 400 550 T800 510 T1200 540' fill='none' stroke='rgba(148,163,184,0.09)' stroke-width='1'/%3E%3Cellipse cx='150' cy='200' rx='350' ry='250' fill='rgba(59,130,246,0.08)' /%3E%3Cellipse cx='950' cy='600' rx='400' ry='300' fill='rgba(99,102,241,0.07)' /%3E%3Cellipse cx='600' cy='80' rx='250' ry='180' fill='rgba(148,163,184,0.06)' /%3E%3C/svg%3E")
          `,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          pointerEvents: 'none',
        },
      }}
    >
      <Card sx={{ maxWidth: 500, width: '100%', borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 20px 60px rgba(0,0,0,0.3)', position: 'relative', zIndex: 1 }}>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          <HourglassEmptyIcon
            sx={{
              fontSize: 80,
              color: 'warning.main',
              mb: 2,
            }}
          />

          <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
            Registration Pending
          </Typography>

          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Thank you for registering with Capital Management.
          </Typography>

          <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
            Your account is currently awaiting administrator approval. You will receive
            an email notification once your access has been granted.
          </Alert>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            This process typically takes 1-2 business days. If you have any urgent
            inquiries, please contact our support team.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
            <Button
              variant="contained"
              onClick={handleContactSupport}
              sx={{ backgroundColor: '#1e293b', '&:hover': { backgroundColor: '#0f172a' } }}
            >
              Contact Support
            </Button>

            <Button
              variant="outlined"
              onClick={handleBackToLogin}
            >
              Back to Login
            </Button>
          </Box>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 4 }}
          >
            Capital Management System - Client Portal
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PendingApprovalPage;
